import type { AlternativeFilters, LegendItem, Price, PricesResponse } from '@/scopes/prices/domain/prices/types'
import type {
  AlternateFacets,
  APIMessage,
  APIPrice,
  Data,
  PricesByDurationRawResponse,
} from '@/scopes/prices/infrastructure/pricesByDuration/types'
import { FilterSelection, FilterType } from '@/scopes/prices/domain/filters/types'
import { mapMonthValue } from '@/scopes/prices/domain/utils'
import { tk } from '@sunwebgroup/ui-components'
import { IconGlyph } from '@sunwebgroup/ui-components/uds'
import { eachDayOfInterval, format, parseISO } from 'date-fns'

export interface PricesResponseMapper {
  map: (pricesAPIResponse: PricesByDurationRawResponse, filterSelection: FilterSelection) => PricesResponse
}

export function createPricesResponseMapper() {
  const map = (pricesAPIResponse: PricesByDurationRawResponse, filterSelection: FilterSelection) => {
    const { DateFrom = '', DateTo = '' } = filterSelection ?? {}
    const prices: Price[] = []
    const datesSet = new Set<string>()
    const bestValueThreshold = pricesAPIResponse.data.bestValues?.[0]?.threshold
    const highlightInfo = getHighlightInfo(pricesAPIResponse.data)

    Object.values(pricesAPIResponse.data.prices ?? {}).forEach((price) => {
      const newPrice = mapPriceToInfo(price, highlightInfo, bestValueThreshold)
      prices.push(newPrice)
      datesSet.add(price.departureDate)
    })

    const pricesByDuration = getPricesByDuration(prices ?? [])
    const pricesByDurationAndDateByWeeks = DateFrom && DateTo ? getPricesByDurationByWeeks(prices, DateFrom, DateTo) : {}
    const pricesByDurationAndDate = getPricesByDurationAndDate(pricesByDuration)
    const year = getYearFromPrice(prices[0])
    const messages = mapMessages(pricesAPIResponse.data.messages ?? [])
    const dates = Array.from(datesSet).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    const alternativeFilters = mapAlternativeFilter(pricesAPIResponse.data.alternateFacets)

    return {
      prices,
      pricesByDuration,
      pricesByDurationAndDate,
      dates,
      year,
      bestValueThreshold,
      highlightInfo,
      messages,
      alternativeFilters,
      ...(pricesByDurationAndDateByWeeks ? { pricesByDurationAndDateByWeeks } : {}),
    }
  }

  return {
    map,
  }
}

function mapPriceToInfo(price: APIPrice, highlightInfo: LegendItem[], bestValueThreshold?: number): Price {
  const key = `${price.duration}|${price.departureDate}`
  const bestValueScore = price.bestValueScore ?? 0

  const bullet = highlightInfo.find(legendItem => legendItem.code === price.highlightCode) ?? null
  const bulletIcon = bullet ? mapBulletIcon(bullet?.code) : null

  return {
    id: key,
    averagePrice: price.averagePrice,
    totalPrice: price.totalPrice,
    departureDate: price.departureDate,
    duration: price.duration,
    mealplan: price.mealplan,
    bullet,
    isBestValue: Boolean(bestValueThreshold && bestValueScore >= bestValueThreshold),
    ...(bulletIcon ? { bulletIcon } : {}),
    isFlightOnly: price.isFlightOnly ?? false,
  }
}

export function mapBulletIcon(code: string): IconGlyph {
  switch (code) {
    case 'EARLYBIRD':
      return 'ClockMulticolor'
    case 'BEST_VALUE':
      return 'ArrowDownInCircleMulticolor'
    default:
      return 'PercentageInCircleMulticolor'
  }
}

function getPricesByDuration(prices: Price[]): Record<number, Price[]> {
  return prices.reduce<Record<number, Price[]>>((acc, price) => {
    if (!acc[price.duration]) {
      acc[price.duration] = []
    }
    acc[price.duration].push(price)
    return acc
  }, {})
}

function getPricesByDurationByWeeks(prices: Price[], dateFrom: string, dateTo: string): Record<number, Record<string, Price>> {
  const startDate = parseISO(dateFrom)
  const endDate = parseISO(dateTo)

  const datesRangeObj = eachDayOfInterval({ start: startDate, end: endDate }).reduce<Record<string, Price>>((acc, date) =>
    ({ ...acc, [format(date, 'yyyy-MM-dd')]: {} as Price }), {})

  return prices.reduce<Record<number, Record<string, Price>>>((acc, price) => {
    if (!acc[price.duration]) {
      acc[price.duration] = { ...datesRangeObj }
    }
    const { departureDate } = price
    if (departureDate) {
      acc[price.duration][departureDate] = price
    }
    return acc
  }, {})
}

function getPricesByDurationAndDate(pricesByDuration: Record<number, Price[]>): Record<number, Record<string, Price>> {
  return Object.entries(pricesByDuration).reduce<Record<number, Record<string, Price>>>(
    (acc, [duration, pricesForDuration]) => {
      acc[Number(duration)] = pricesForDuration.reduce<Record<string, Price>>(
        (innerAcc: Record<string, Price>, price) => {
          innerAcc[price.departureDate] = price
          return innerAcc
        },
        {},
      )
      return acc
    },
    {},
  )
}

function getHighlightInfo(data: Data): LegendItem[] {
  const highlightInfo: LegendItem[] = [...(data.highlightInfo ?? [])]

  if (data.bestValuesCaption) {
    highlightInfo.push({
      code: 'BEST_VALUE',
      description: data.bestValuesCaption.title,
      hexColor: tk('color_semantic_border_success'),
      isBestValue: true,
    })
  }

  return highlightInfo
}

function getYearFromPrice(price: Price) {
  if (!price)
    return ''

  return new Date(price.departureDate).getFullYear().toString()
}

function mapMessages(messages: APIMessage[]): APIMessage[] {
  const filteredMessages = messages.filter(message => message.texts && message.texts.some(text => text !== ''))
  return filteredMessages.map((message) => {
    message.texts = message.texts.filter(text => text !== '')
    return message
  })
}

const filterKeyMap: Record<string, keyof FilterType> = {
  durations: 'Duration',
  months: 'Month',
  roomTypes: 'RoomType',
  departureAirports: 'DepartureAirport',
  mealplans: 'Mealplan',
  transportTypes: 'TransportType',
}
function mapAlternativeFilter(facets: AlternateFacets | undefined): AlternativeFilters | null {
  if (!facets)
    return null

  const result = Object.keys(facets).reduce<AlternativeFilters>((acc, key) => {
    if (facets[key].options.length === 0)
      return acc
    const newKey = filterKeyMap[key]
    acc[newKey] = {
      title: facets[key].description,
      options: facets[key].options.map(option => ({
        text: option.caption,
        value: newKey === 'Month' ? mapMonthValue(option.value) : option.value,
      })),
    }

    return acc
  }, {})

  return Object.keys(result).length ? result : null
}
